import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Injector, resource } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { LocaleService, TenantService } from '@cmi/addin-core';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import {
  AppDetail,
  AppDetailResult,
  Assoc,
  ContentProvider,
  ContentProviderParameter,
  ContentProviderProperty,
  GroupedSearchResult,
  RootType,
  SearchResult
} from './home.models';

@Injectable({
  providedIn: 'root'
})
export class HomeService {
  private readonly _http = inject(HttpClient);
  private readonly _localeService = inject(LocaleService);
  private readonly _injector = inject(Injector);
  private readonly _tenantService = inject(TenantService);
  private readonly _emptyAppKeyPlaceholder = 'misc';

  private readonly _rootTypeResource = resource<RootType, { tenant: string | null }>({
    params: () => ({
      tenant: this._tenantService.selectedTenantName()
    }),
    loader: async () => {
      const result = await firstValueFrom(
        this._http.get<RootType>(
          `/api/core/Model/GetModelSection?typeKey=Dokument&includeFields=true&includeAssocs=true&usage=Document`
        )
      );

      // Process contentProviders to ensure empty appKeys get placeholder
      const processedContentProviders =
        result.contentProvider?.map((provider) => ({
          ...provider,
          appKeys:
            provider.appKeys && provider.appKeys.length > 0
              ? provider.appKeys
              : [this._emptyAppKeyPlaceholder]
        })) ?? [];

      const newResult: RootType = {
        ...result,
        contentProvider: processedContentProviders
      };
      return newResult;
    }
  });

  public readonly rootType = computed(() => this._rootTypeResource.value());
  public readonly rootTypeLoading = computed(() => this._rootTypeResource.isLoading());
  public readonly rootTypeError = computed(() => this._rootTypeResource.error());
  public readonly rootTypeHasValue = computed(() => this._rootTypeResource.hasValue());

  public readonly appKeys = computed(() => {
    const rootType = this.rootType();
    if (!rootType) {
      return [];
    }

    const keys = Array.from(
      new Set((rootType.contentProvider ?? []).flatMap((provider) => provider.appKeys ?? []))
    );

    return keys;
  });

  private readonly _appDetailsResource = resource<
    AppDetailResult[],
    { appKeys: string[]; tenant: string | null }
  >({
    params: () => ({
      appKeys: [...this.appKeys(), 'reports'],
      tenant: this._tenantService.selectedTenantName()
    }),
    loader: async (param) => {
      // Only fetch if we have appKeys
      if (param.params.appKeys.length === 0 || !param.params.tenant) {
        return [];
      }

      const selectedTenant = this._tenantService.getTenantByName(param.params.tenant);
      if (!selectedTenant) {
        return [];
      }

      // For each appKey, fetch AppDetails in parallel
      const appDetailsRequests = param.params.appKeys.map((appKey) =>
        firstValueFrom(this._fetchAppDetails(appKey, selectedTenant.apiUrl))
      );

      const results = await Promise.allSettled(appDetailsRequests);
      const details = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value)
        .flat();

      return details.sort((a, b) => a.sortOrder - b.sortOrder);
    }
  });

  public readonly appDetails = computed(
    () => this._appDetailsResource.value()?.filter((d) => d.name !== 'reports') ?? []
  );
  public readonly reportsAppDetails = computed(() =>
    this._appDetailsResource.value()?.find((d) => d.name === 'reports')
  );
  public readonly appDetailsLoading = computed(() => this._appDetailsResource.isLoading());
  public readonly appDetailsError = computed(() => this._appDetailsResource.error());
  public readonly appDetailsHasValue = computed(() => this._appDetailsResource.hasValue());

  public reload(): void {
    this._rootTypeResource.reload();
    this._appDetailsResource.reload();
  }

  public getContentProvidersByTag(tag: string): Observable<ContentProvider[]> {
    return toObservable(this.rootType, { injector: this._injector }).pipe(
      map((d) => {
        if (!d) {
          return [];
        }
        return (d.contentProvider ?? [])
          .filter((provider) => (provider.appKeys ?? []).includes(tag))
          .sort((a, b) => a.key.localeCompare(b.key));
      })
    );
  }

  public getContentProviderPropertiesByKey(key: string): Observable<ContentProviderProperty[]> {
    return toObservable(this.rootType, { injector: this._injector }).pipe(
      map((d) => {
        if (!d) {
          return [];
        }
        const provider = (d.contentProvider ?? []).find((p) => p.key === key);
        return provider?.properties ?? [];
      })
    );
  }

  public getContentProvider(tag: string, key: string): Observable<ContentProvider | null> {
    return toObservable(this.rootType, { injector: this._injector }).pipe(
      map((d) => {
        if (!d) {
          return null;
        }
        const provider = (d.contentProvider ?? []).find((p) => {
          return p.key === key && (p.appKeys ?? []).includes(tag);
        });
        if (!provider) {
          return null;
        }
        return {
          ...provider,
          properties: (provider.properties ?? []).sort((a, b) => a.key.localeCompare(b.key)),
          parameters: (provider.parameters ?? []).sort((a, b) => a.name.localeCompare(b.name))
        };
      })
    );
  }

  public getContentProviderSignal(tag: string, key: string): ContentProvider | null {
    const rootType = this.rootType();
    if (!rootType) {
      return null;
    }
    const provider = (rootType.contentProvider ?? []).find((p) => {
      return p.key === key && (p.appKeys ?? []).includes(tag);
    });
    if (!provider) {
      return null;
    }
    return {
      ...provider,
      properties: (provider.properties ?? []).sort((a, b) => a.key.localeCompare(b.key)),
      parameters: (provider.parameters ?? []).sort((a, b) => a.name.localeCompare(b.name))
    };
  }

  public getContentProviderParameter(
    tag: string,
    key: string,
    paramName: string
  ): ContentProviderParameter | null {
    const rootType = this.rootType();
    if (!rootType) {
      return null;
    }
    const provider = (rootType.contentProvider ?? []).find((p) => {
      return p.key === key && (p.appKeys ?? []).includes(tag);
    });
    if (!provider) {
      return null;
    }
    const parameter = (provider.parameters ?? []).find((p) => p.name === paramName);
    return parameter ?? null;
  }

  public getAppDetail(appKey: string): AppDetailResult | undefined {
    return this.appDetails().find((detail) => detail.name === appKey);
  }

  public searchProperties(searchTerm: string): SearchResult[] {
    const rootType = this.rootType();
    if (!rootType || !searchTerm.trim()) {
      return [];
    }

    const results: SearchResult[] = [];
    const lowerSearchTerm = searchTerm.toLowerCase();

    for (const provider of rootType.contentProvider ?? []) {
      for (const property of provider.properties ?? []) {
        // Search in property key, name and typeName
        if (
          property.key.toLowerCase().includes(lowerSearchTerm) ||
          property.name.toLowerCase().includes(lowerSearchTerm) ||
          property.typeName.toLowerCase().includes(lowerSearchTerm)
        ) {
          // Add result for each appKey this provider belongs to
          for (const appKey of provider.appKeys ?? []) {
            results.push({
              appKey,
              providerKey: provider.key,
              property
            });
          }
        }
      }
    }

    return results;
  }

  public searchPropertiesGrouped(searchTerm: string, tag?: string): GroupedSearchResult[] {
    const rootType = this.rootType();
    if (!rootType || !searchTerm.trim()) {
      return [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const appKeyMap = new Map<string, GroupedSearchResult>();

    for (const provider of rootType.contentProvider ?? []) {
      const matchingProperties: ContentProviderProperty[] = [];

      // Find all matching properties in this provider
      for (const property of provider.properties ?? []) {
        if (
          property.key.toLowerCase().includes(lowerSearchTerm) ||
          property.name.toLowerCase().includes(lowerSearchTerm) ||
          property.typeName.toLowerCase().includes(lowerSearchTerm)
        ) {
          matchingProperties.push(property);
        }
      }

      // If we found matching properties, add to each appKey group
      if (matchingProperties.length > 0) {
        for (const appKey of provider.appKeys ?? []) {
          // Filter by tag if provided
          if (tag && appKey !== tag) {
            continue;
          }

          // Get or create the app group
          if (!appKeyMap.has(appKey)) {
            const detail = this.getAppDetail(appKey);
            appKeyMap.set(appKey, {
              appKey: detail?.title ?? appKey,
              app: detail!,
              providers: []
            });
          }

          // Add this provider with its matching properties to the app group
          appKeyMap.get(appKey)!.providers.push({
            providerKey: provider.key,
            properties: [...matchingProperties.sort((a, b) => a.key.localeCompare(b.key))]
          });
        }
      }
    }

    const searchResults = Array.from(appKeyMap.values())
      .filter((g) => g.app)
      .sort((a, b) => a.app.sortOrder - b.app.sortOrder);
    const sortedResults = searchResults.map((group) => {
      return {
        ...group,
        providers: group.providers.sort((a, b) => a.providerKey.localeCompare(b.providerKey))
      };
    });

    return sortedResults;
  }

  public getAssocs(): Assoc[] {
    const rootType = this.rootType();
    if (!rootType) {
      return [];
    }
    return rootType.assocs ?? [];
  }

  public getAllowedTypes(typeKey: string): Observable<RootType> {
    return this._http.get<RootType>(
      `/api/core/Model/GetModelSection?typeKey=${typeKey}&includeFields=true&includeAssocs=true&usage=Document`
    );
  }

  private _fetchAppDetails(appKey: string, tenantUrl: string): Observable<AppDetailResult[]> {
    if (appKey === this._emptyAppKeyPlaceholder) {
      return of([
        {
          name: this._emptyAppKeyPlaceholder,
          title: this._localeService.translate('home.misc'),
          iconId: '',
          externalLink: '',
          iconDataUrl: '/folder.svg',
          sortOrder: 999
        } satisfies AppDetailResult
      ]);
    }

    const language = this._localeService.getApiLanguage();

    return this._http
      .get<AppDetail[]>(`/api/core/Model/GetAppDetails?appKey=${appKey}&language=${language}`)
      .pipe(
        map((appDetails) =>
          appDetails.map((detail) => ({
            ...detail,
            iconDataUrl: `${tenantUrl}/api/core/Icon/GetIcon?iconId=${detail.iconId}`
          }))
        ),
        catchError((error) => {
          console.warn(`Failed to fetch app details for appKey: ${appKey}`, error);
          return of([]);
        })
      );
  }
}
